import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Divider, CircularProgress,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Box, Chip, LinearProgress, Checkbox, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import WifiTetheringIcon from '@material-ui/icons/WifiTethering';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import ImageIcon from '@material-ui/icons/Image';
import AudiotrackIcon from '@material-ui/icons/Audiotrack';
import VideocamIcon from '@material-ui/icons/Videocam';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import MainContainer from '../../components/MainContainer';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(3),
    gap: theme.spacing(2),
    overflow: 'auto',
    boxSizing: 'border-box',
  },
  descriptionBox: {
    background: 'linear-gradient(135deg, #00875A15 0%, #00C97A10 100%)',
    border: '1.5px solid #00875A40',
    borderRadius: 12,
    padding: theme.spacing(2.5),
    flexShrink: 0,
  },
  descriptionTitle: {
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#00875A',
    marginBottom: theme.spacing(1),
  },
  descriptionText: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: theme.mode === 'light' ? '#0B1120' : '#E8EAED',
    lineHeight: 1.6,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 800,
    color: theme.mode === 'light' ? '#0B1120' : '#E8EAED',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  flowsList: {
    maxHeight: 300,
    overflow: 'auto',
  },
  messageBlock: {
    background: theme.mode === 'light' ? '#F4F5F7' : '#1A1D2E',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    position: 'relative',
  },
  delayRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    alignItems: 'center',
  },
  delayLabel: {
    fontSize: '0.75rem',
    color: theme.mode === 'light' ? '#6B778C' : '#8892A4',
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  flowsContainer: {
    flex: 1,
    overflow: 'auto',
  },
}));

const FollowUp = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [flows, setFlows] = useState([]);

  // Flow manager dialog
  const [flowsOpen, setFlowsOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [currentFlowType, setCurrentFlowType] = useState('qrcode');
  const [editFlow, setEditFlow] = useState(null);
  const [flowName, setFlowName] = useState('');
  const [flowMessages, setFlowMessages] = useState([{ msgs: [{ type: 'text', content: '', url: '', filename: '' }], delayDays: 0, delayHours: 0, delayMinutes: 0, order: 0 }]);
  const [saving, setSaving] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [connModalOpen, setConnModalOpen] = useState(false);
  const [connFlow, setConnFlow] = useState(null);
  const [selectedConns, setSelectedConns] = useState([]);
  const [availableWhatsapps, setAvailableWhatsapps] = useState([]);
  const [apiTemplates, setApiTemplates] = useState([]);
  // Schedule per flow
  // Per-day schedule state
  const [wdEnabled,  setWdEnabled]  = useState(true); // Seg-Sex
  const [wdStart,    setWdStart]    = useState(9);
  const [wdEnd,      setWdEnd]      = useState(18);
  const [satEnabled, setSatEnabled] = useState(false); // Sábado
  const [satStart,   setSatStart]   = useState(9);
  const [satEnd,     setSatEnd]     = useState(13);
  const [sunEnabled, setSunEnabled] = useState(false); // Domingo
  const [sunStart,   setSunStart]   = useState(0);
  const [sunEnd,     setSunEnd]     = useState(23);

  useEffect(() => { loadFlows(); loadApiTemplates(); }, []);

  const loadApiTemplates = async () => {
    try {
      const { data } = await api.get('/broadcast/templates');
      setApiTemplates(data || []);
    } catch (e) {}
  };

  const loadFlows = async () => {
    setLoading(true);
    try {
      const [flowsRes] = await Promise.all([
        api.get('/follow-up/flows'),
        api.post('/follow-up/ensure-lanes'),
      ]);
      setFlows(flowsRes.data.flows || []);
    } catch (err) {
      toast.error('Erro ao carregar fluxos');
    } finally {
      setLoading(false);
    }
  };

  const openConnModal = async (flow) => {
    try {
      const { data } = await api.get('/whatsapp/');
      setAvailableWhatsapps(data);
    } catch (e) {}
    setConnFlow(flow);
    setSelectedConns(flow.whatsappIds || []);
    setConnModalOpen(true);
  };

  const saveConnections = async () => {
    if (!connFlow) return;
    try {
      await api.put(`/follow-up/flows/${connFlow.id}/connections`, { whatsappIds: selectedConns });
      setFlows(prev => prev.map(f => f.id === connFlow.id ? { ...f, whatsappIds: selectedConns } : f));
      toast.success('Conexões salvas!');
    } catch (e) { toast.error('Erro ao salvar conexões'); }
    setConnModalOpen(false);
  };

  const openTypeModal = () => {
    setTypeModalOpen(true);
  };

  const handleTypeSelected = (type) => {
    setTypeModalOpen(false);
    setCurrentFlowType(type);
    setEditFlow(null);
    setFlowName('');
    setFlowMessages([
      { msgs: [{ type: 'text', content: 'Olá {{name}}! Tudo bem? Passando para saber se ficou alguma dúvida sobre nossa conversa. Estou à disposição para ajudar!', url: '', filename: '' }], delayDays: 0, delayHours: 0, delayMinutes: 5, order: 0 },
      { msgs: [{ type: 'text', content: 'Oi {{name}}, somente reforçando o contato. Tem alguma informação adicional que posso te passar?', url: '', filename: '' }], delayDays: 1, delayHours: 0, delayMinutes: 0, order: 1 },
      { msgs: [{ type: 'text', content: '{{name}}, que tal uma rápida conversa? Posso te mostrar melhor o que temos. Me chama quando tiver um tempinho!', url: '', filename: '' }], delayDays: 1, delayHours: 0, delayMinutes: 0, order: 2 },
      { msgs: [{ type: 'text', content: 'Oi {{name}}! Ainda estou aqui caso queira conversar. Qualquer dúvida, é só falar.', url: '', filename: '' }], delayDays: 1, delayHours: 0, delayMinutes: 0, order: 3 },
      { msgs: [{ type: 'text', content: '{{name}}, passando para deixar em aberto nossa proposta. Qualquer ajuste que precisar, podemos adaptar conforme sua necessidade.', url: '', filename: '' }], delayDays: 1, delayHours: 0, delayMinutes: 0, order: 4 },
      { msgs: [{ type: 'text', content: 'Olá {{name}}! Temos algumas novidades que podem te interessar. Quando tiver um momento, me chama para contar!', url: '', filename: '' }], delayDays: 1, delayHours: 0, delayMinutes: 0, order: 5 },
      { msgs: [{ type: 'text', content: '{{name}}, esta será minha última tentativa de contato por enquanto. Se mudar de ideia ou precisar de algo, é só nos chamar. Boa sorte!', url: '', filename: '' }], delayDays: 1, delayHours: 0, delayMinutes: 0, order: 6 },
    ]);
    setWdEnabled(true);   setWdStart(9);  setWdEnd(18);
    setSatEnabled(false); setSatStart(9); setSatEnd(13);
    setSunEnabled(false); setSunStart(0); setSunEnd(23);
    setFlowsOpen(true);
  };

  const openEditFlow = (flow) => {
    setCurrentFlowType(flow.flowType || 'qrcode');
    setEditFlow(flow);
    setFlowName(flow.name);
    try {
      const parsed = JSON.parse(flow.messages);
      const migrated = parsed.map(s => {
        const base = s.msgs && Array.isArray(s.msgs) ? s : { ...s, msgs: [{ type: 'text', content: s.message || '', url: '', filename: '' }] };
        return { ...base, apiTemplateId: base.apiTemplateId || null };
      });
      setFlowMessages(migrated);
    } catch { setFlowMessages([]); }
    const _days = Array.isArray(flow.scheduleDays) ? flow.scheduleDays :
      (flow.scheduleDays ? (() => { try { return JSON.parse(flow.scheduleDays); } catch { return []; } })() : []);
    setWdEnabled(_days.some(d => d >= 1 && d <= 5));
    setWdStart(flow.scheduleStart    !== undefined ? Number(flow.scheduleStart)    : 9);
    setWdEnd(flow.scheduleEnd        !== undefined ? Number(flow.scheduleEnd)      : 18);
    setSatEnabled(_days.includes(6));
    setSatStart(flow.scheduleStartSat !== undefined ? Number(flow.scheduleStartSat) : 9);
    setSatEnd(flow.scheduleEndSat     !== undefined ? Number(flow.scheduleEndSat)   : 13);
    setSunEnabled(_days.includes(0));
    setSunStart(flow.scheduleStartSun !== undefined ? Number(flow.scheduleStartSun) : 0);
    setSunEnd(flow.scheduleEndSun     !== undefined ? Number(flow.scheduleEndSun)   : 23);
    setFlowsOpen(true);
  };

  const addMessage = () => {
    setFlowMessages(prev => [...prev, { msgs: [{ type: 'text', content: '', url: '', filename: '' }], delayDays: 1, delayHours: 0, delayMinutes: 0, order: prev.length }]);
  };

  const removeMessage = (idx) => {
    setFlowMessages(prev => prev.filter((_, i) => i !== idx));
  };

  const updateMessage = (idx, field, value) => {
    setFlowMessages(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const addMsgToStep = (stepIdx, type) => {
    setFlowMessages(prev => prev.map((s, i) => {
      if (i !== stepIdx) return s;
      return { ...s, msgs: [...(s.msgs || []), { type, content: '', url: '', filename: '' }] };
    }));
  };

  const removeMsgFromStep = (stepIdx, msgIdx) => {
    setFlowMessages(prev => prev.map((s, i) => {
      if (i !== stepIdx) return s;
      const newMsgs = (s.msgs || []).filter((_, mi) => mi !== msgIdx);
      return { ...s, msgs: newMsgs.length ? newMsgs : [{ type: 'text', content: '', url: '', filename: '' }] };
    }));
  };

  const updateMsgInStep = (stepIdx, msgIdx, field, value) => {
    setFlowMessages(prev => prev.map((s, i) => {
      if (i !== stepIdx) return s;
      const newMsgs = (s.msgs || []).map((m, mi) => mi === msgIdx ? { ...m, [field]: value } : m);
      return { ...s, msgs: newMsgs };
    }));
  };

  const handleFileUpload = async (stepIdx, msgIdx, file, type) => {
    if (type === 'document') {
      const isPdf = file.type === 'application/pdf' || String(file.name || '').toLowerCase().endsWith('.pdf');
      if (!isPdf) return toast.error('Material deve ser um arquivo PDF');
      if (file.size > 50 * 1024 * 1024) return toast.error('PDF deve ter no máximo 50 MB');
    }
    updateMsgInStep(stepIdx, msgIdx, 'uploading', true);
    try {
      const form = new FormData();
      form.append('typeArch', 'broadcast');
      form.append('file', file);
      const res = await api.post('/broadcast/media-upload', form);
      updateMsgInStep(stepIdx, msgIdx, 'url', res.data.url);
      updateMsgInStep(stepIdx, msgIdx, 'filename', res.data.originalname || file.name);
      updateMsgInStep(stepIdx, msgIdx, 'uploading', false);
    } catch (err) {
      toast.error('Erro ao fazer upload do arquivo');
      updateMsgInStep(stepIdx, msgIdx, 'uploading', false);
    }
  };

  const saveFlow = async () => {
    if (!flowName.trim()) return toast.error('Informe o nome do fluxo');
    const firstStep = flowMessages[0];
    const firstMsgs = firstStep ? (firstStep.msgs || []) : [];
    const hasContent = firstMsgs.some(m => (m.type === 'text' && (m.content || '').trim()) || (m.type !== 'text' && m.url));
    if (!flowMessages.length || !hasContent) return toast.error('Adicione pelo menos uma mensagem no primeiro passo');
    if (wdEnabled && wdEnd < wdStart) return toast.error('Horário final de Seg-Sex deve ser maior que o inicial');
    if (satEnabled && satEnd < satStart) return toast.error('Horário final de Sábado deve ser maior que o inicial');
    if (sunEnabled && sunEnd < sunStart) return toast.error('Horário final de Domingo deve ser maior que o inicial');
    if (currentFlowType === 'official' && flowMessages.some(step => !step.apiTemplateId)) {
      return toast.error('Selecione um template Meta aprovado em cada passo');
    }
    setSaving(true);
    try {
      const msgs = flowMessages.map((m, i) => ({ ...m, order: i }));
      const _scheduleDays = [
        ...(wdEnabled  ? [1,2,3,4,5] : []),
        ...(satEnabled ? [6] : []),
        ...(sunEnabled ? [0] : []),
      ];
      const schedulePayload = {
        scheduleDays: _scheduleDays,
        scheduleStart: wdStart, scheduleEnd: wdEnd,
        scheduleStartSat: satStart, scheduleEndSat: satEnd,
        scheduleStartSun: sunStart, scheduleEndSun: sunEnd,
      };
      if (editFlow) {
        const response = await api.put(`/follow-up/flows/${editFlow.id}`, { name: flowName, messages: msgs, ...schedulePayload });
        if (response.data?.warnings?.length) {
          toast.warning(`Fluxo salvo, mas ${response.data.warnings.length} material(is) precisam ser reenviados antes de iniciar.`);
        } else {
          toast.success('Fluxo atualizado!');
        }
      } else {
        await api.post('/follow-up/flows', { name: flowName, messages: msgs, flowType: currentFlowType, ...schedulePayload });
        toast.success('Fluxo criado!');
      }
      setFlowsOpen(false);
      await loadFlows();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Erro ao salvar fluxo');
    } finally {
      setSaving(false);
    }
  };

  const deleteFlow = async (id) => {
    try {
      await api.delete(`/follow-up/flows/${id}`);
      toast.success('Fluxo removido');
      await loadFlows();
    } catch { toast.error('Erro ao remover fluxo'); }
  };

  return (
    <MainContainer>
    <div className={classes.root}>
      {/* Description Banner */}
      <div className={classes.descriptionBox}>
        <Typography className={classes.descriptionTitle}>Como funciona</Typography>
        <Typography className={classes.descriptionText}>
          ESSE É O FLUXO AUTOMÁTICO DE MENSAGENS QUE SERÁ DISPARADO PARA TODOS OS LEADS QUE VOCÊ CLICAR
          DENTRO DO CHAT DELES PARA ACIONAR O FOLLOW UP{' '}
          <span style={{ fontWeight: 400, fontSize: '0.88em' }}>
            (dentro do chat do cliente tem o botão para você escolher o follow up e ativar)
          </span>.
        </Typography>
        <Typography style={{ fontSize: '0.84rem', lineHeight: 1.55, color: 'inherit', marginTop: 10, background: 'rgba(255,152,0,0.1)', borderLeft: '3px solid #FF9800', padding: '8px 12px', borderRadius: 4 }}>
          ✅ Funciona com <b>QR Code e WhatsApp API Oficial</b>. QR Code aceita texto, imagem, áudio, vídeo e PDF livres. Na API Oficial, cada passo usa um template Meta aprovado; quando o template possui mídia no cabeçalho, essa mídia aprovada é enviada junto.
        </Typography>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Typography style={{ fontSize: '0.84rem', lineHeight: 1.55, color: 'inherit' }}>
            ✅ Ao ativar, o lead vai automaticamente para a aba Kanban <b>"Fluxo Follow-up"</b> e fica lá enquanto estiver recebendo as mensagens.
          </Typography>
          <Typography style={{ fontSize: '0.84rem', lineHeight: 1.55, color: 'inherit' }}>
            💬 Se o cliente responder a qualquer momento, o sistema retira ele do Kanban <b>"Fluxo Follow-up"</b> e o leva para <b>"Aguardando"</b> no chat para o atendimento continuar.
          </Typography>
          <Typography style={{ fontSize: '0.84rem', lineHeight: 1.55, color: 'inherit' }}>
            ⏳ Se o fluxo terminar sem resposta, o lead é movido para <b>"Sem Sucesso Follow-up"</b> e chega também no <b>"Aguardando"</b> para o usuário colocar em outro fluxo ou decidir o que fazer.
          </Typography>
          <Typography style={{ fontSize: '0.84rem', lineHeight: 1.55, color: 'inherit' }}>
            🔄 Você pode criar quantos fluxos quiser. Follow up para lead frio, follow up para lead quente, múltiplos Follow up — acabando um, pode ativar outro. Use livremente como preferir na sua operação comercial.
          </Typography>
          <Typography style={{ fontSize: '0.82rem', marginTop: 4, opacity: 0.65, lineHeight: 1.4 }}>
            📌 As duas abas Kanban ("Fluxo Follow-up" e "Sem Sucesso Follow-up") já ficam salvas automaticamente no seu Kanban.
          </Typography>
        </div>
      </div>

      {/* Flows Section */}
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>Fluxos de Follow-up</Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={openTypeModal}
          style={{ borderRadius: 8, textTransform: 'none', fontWeight: 700 }}
        >
          + Novo Fluxo
        </Button>
      </div>

      <Divider />

      {/* Info box */}
      <Box style={{
        background: "linear-gradient(135deg, rgba(0,135,90,0.07) 0%, rgba(0,135,90,0.03) 100%)",
        border: "1px solid rgba(0,135,90,0.2)",
        borderRadius: 10, padding: "10px 14px",
      }}>
        <Typography style={{ fontSize: "0.82rem", fontWeight: 700, color: "#00875A", marginBottom: 4 }}>
          📋 Conexões e canais no Follow-up
        </Typography>
        {!infoExpanded && (
          <Box onClick={() => setInfoExpanded(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", marginTop: 2,
              background: "rgba(0,135,90,0.1)", border: "1px solid rgba(0,135,90,0.3)",
              borderRadius: 20, padding: "2px 10px" }}>
            <Typography style={{ fontSize: "0.72rem", color: "#00875A", fontWeight: 700 }}>
              ▼ Leia tudo — como funciona · Clique aqui
            </Typography>
          </Box>
        )}
        {infoExpanded && (
          <Typography style={{ fontSize: "0.78rem", color: "#555", lineHeight: 1.8 }}>
            Por padrão, um fluxo de Follow-up funciona em <strong>todas as conexões</strong> (WhatsApp, Instagram e Facebook). Use o botão de conexões em cada fluxo para restringir — por exemplo, um fluxo só para o WhatsApp Comercial.<br/><br/>
            <strong>📱 Fluxo geral (texto + IA)</strong> — Se o seu Follow-up usa apenas texto e agente de IA, pode deixar sem restrição e ele funcionará em WhatsApp, Instagram e Facebook ao mesmo tempo.<br/><br/>
            <strong>⚠️ Fluxo com áudio, vídeo, imagem ou material PDF</strong> — Esses recursos funcionam apenas no WhatsApp. No Instagram e Facebook (regra da Meta, não do sistema), esses arquivos não são entregues no chat. Para esses casos, crie um fluxo separado para WhatsApp e outro só com texto para Instagram/Facebook, e use o botão de conexões para definir em qual canal cada fluxo roda.<br/><br/>
            <span onClick={() => setInfoExpanded(false)}
              style={{ cursor: "pointer", color: "#00875A", fontWeight: 700, fontSize: "0.72rem" }}>
              ▲ Recolher
            </span>
          </Typography>
        )}
      </Box>

      {/* Flows List */}
      <div className={classes.flowsContainer}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <CircularProgress size={28} />
          </div>
        ) : flows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, opacity: 0.5 }}>
            <Typography style={{ fontSize: '0.9rem' }}>Nenhum fluxo criado ainda.</Typography>
            <Typography style={{ fontSize: '0.8rem', marginTop: 8 }}>Clique em "+ Novo Fluxo" para começar.</Typography>
          </div>
        ) : (
          <List dense>
            {flows.map(flow => (
              <ListItem key={flow.id} divider style={{ borderRadius: 8, marginBottom: 4 }}>
                <ListItemText
                  primary={<span style={{ fontWeight: 700 }}>{flow.name}</span>}
                  secondary={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                      <span>{(() => { try { return JSON.parse(flow.messages).length + ' passo(s)'; } catch { return '0 passos'; } })()}</span>
                      <Chip
                        size="small"
                        label={(flow.flowType || 'qrcode') === 'official' ? 'WhatsApp API Meta' : 'QR Code'}
                        style={{
                          fontSize: '0.62rem', height: 20,
                          background: (flow.flowType || 'qrcode') === 'official' ? 'rgba(25,118,210,0.12)' : 'rgba(0,135,90,0.08)',
                          color: (flow.flowType || 'qrcode') === 'official' ? '#1976d2' : '#00875A',
                          border: (flow.flowType || 'qrcode') === 'official' ? '1px solid rgba(25,118,210,0.3)' : '1px solid rgba(0,135,90,0.2)',
                          fontWeight: 700,
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<WifiTetheringIcon style={{ fontSize: 13 }} />}
                        label={(flow.whatsappIds && flow.whatsappIds.length > 0) ? `${flow.whatsappIds.length} conexão(ões)` : 'Todas as conexões'}
                        onClick={(e) => { e.stopPropagation(); openConnModal(flow); }}
                        style={{
                          cursor: 'pointer', fontSize: '0.62rem', height: 20,
                          background: (flow.whatsappIds && flow.whatsappIds.length > 0) ? 'rgba(0,135,90,0.12)' : 'rgba(0,0,0,0.05)',
                          color: (flow.whatsappIds && flow.whatsappIds.length > 0) ? '#00875A' : 'inherit',
                          border: (flow.whatsappIds && flow.whatsappIds.length > 0) ? '1px solid rgba(0,135,90,0.3)' : '1px solid rgba(0,0,0,0.1)',
                        }}
                      />
                    </span>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => openEditFlow(flow)} title="Editar">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => deleteFlow(flow.id)} title="Remover">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </div>

      {/* Flow Editor Dialog */}
      <Dialog
        open={flowsOpen}
        onClose={() => setFlowsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}
      >
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>
            {editFlow ? 'Editar Fluxo' : 'Novo Fluxo'}
            <Chip size="small" label={currentFlowType === 'official' ? 'WhatsApp API Meta' : 'QR Code'}
              style={{ marginLeft: 10, fontSize: '0.65rem', height: 20,
                background: currentFlowType === 'official' ? 'rgba(25,118,210,0.12)' : 'rgba(0,135,90,0.08)',
                color: currentFlowType === 'official' ? '#1976d2' : '#00875A',
                border: currentFlowType === 'official' ? '1px solid rgba(25,118,210,0.3)' : '1px solid rgba(0,135,90,0.2)',
                fontWeight: 700 }} />
          </span>
          <IconButton size="small" onClick={() => setFlowsOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!editFlow && (
            <div style={{
              background: 'rgba(0,135,90,0.08)',
              border: '1px solid rgba(0,135,90,0.25)',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
            }}>
              <Typography variant="caption" style={{ display: 'block', fontWeight: 700, color: '#00875A', marginBottom: 4 }}>
                {currentFlowType === 'official' ? '🔵 Fluxo API Meta' : '💡 Recomendação'}
              </Typography>
              <Typography variant="caption" style={{ display: 'block', lineHeight: 1.55 }}>
                {currentFlowType === 'official' ? <>Cada passo enviará um <b>template aprovado pela Meta</b>. Selecione o template em cada passo. Defina o intervalo de envio entre passos.</> : <>Recomendamos pelo menos <b>7 dias com 1 mensagem por dia</b>. Já deixamos modelos prontos em cada card abaixo, mas você pode alterar o texto como preferir. Use <b>{'{{name}}'}</b> para inserir o nome do contato.</>
              }</Typography>
            </div>
          )}
          <TextField
            label="Nome do fluxo"
            value={flowName}
            onChange={e => setFlowName(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginBottom: 16 }}
            placeholder="Ex: Follow-up Comercial 7 Dias"
          />


          {/* Schedule configuration: unrestricted, or day groups + required windows */}
          <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(0,0,0,0.03)', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}>
            <Typography variant="caption" style={{ fontWeight: 700, display: 'block', marginBottom: 4, color: '#444' }}>
              📅 Dias e horários permitidos
            </Typography>
            {!(wdEnabled || satEnabled || sunEnabled) ? (
              <div style={{
                margin: '8px 0 10px',
                padding: '9px 11px',
                borderRadius: 6,
                background: 'rgba(245,124,0,0.10)',
                border: '1px solid rgba(245,124,0,0.45)',
              }}>
                <Typography variant="caption" style={{ color: '#9a4d00', display: 'block', fontWeight: 700, lineHeight: 1.5 }}>
                  ⚠️ Sem dias marcados, o follow-up envia em qualquer dia e horário.
                </Typography>
              </div>
            ) : (
              <div style={{
                margin: '8px 0 10px',
                padding: '9px 11px',
                borderRadius: 6,
                background: 'rgba(0,135,90,0.08)',
                border: '1px solid rgba(0,135,90,0.35)',
              }}>
                <Typography variant="caption" style={{ color: '#006b48', display: 'block', fontWeight: 700, lineHeight: 1.5 }}>
                  ✓ Restrição ativa: o envio ocorre somente nos grupos marcados e dentro dos horários definidos.
                </Typography>
              </div>
            )}
            <Typography variant="caption" style={{ color: '#777', display: 'block', marginBottom: 10, lineHeight: 1.5 }}>
              Marque o grupo de dias para ativar seus horários. Segunda a sexta compartilha uma única janela. Horário de Brasília.
            </Typography>
            {[
              { label: 'Seg — Sex', enabled: wdEnabled,  setEnabled: setWdEnabled,  start: wdStart,  setStart: setWdStart,  end: wdEnd,  setEnd: setWdEnd  },
              { label: 'Sábado',    enabled: satEnabled, setEnabled: setSatEnabled, start: satStart, setStart: setSatStart, end: satEnd, setEnd: setSatEnd },
              { label: 'Domingo',   enabled: sunEnabled, setEnabled: setSunEnabled, start: sunStart, setStart: setSunStart, end: sunEnd, setEnd: setSunEnd },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <FormControlLabel
                  style={{ minWidth: 110, margin: 0 }}
                  control={<Checkbox checked={row.enabled} onChange={e => row.setEnabled(e.target.checked)} color="primary" size="small" />}
                  label={<span style={{ fontSize: '0.82rem', fontWeight: 700 }}>{row.label}</span>}
                />
                <TextField select size="small" label="Das" value={row.start}
                  onChange={e => row.setStart(Number(e.target.value))}
                  variant="outlined" style={{ width: 95 }} disabled={!row.enabled}>
                  {Array.from({ length: 24 }, (_, i) => i).map(h => (
                    <MenuItem key={h} value={h}>{String(h).padStart(2,'0')}:00</MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" style={{ color: '#888' }}>às</Typography>
                <TextField select size="small" label="Às" value={row.end}
                  onChange={e => row.setEnd(Number(e.target.value))}
                  variant="outlined" style={{ width: 95 }} disabled={!row.enabled}>
                  {Array.from({ length: 24 }, (_, i) => i).map(h => (
                    <MenuItem key={h} value={h}>{String(h).padStart(2,'0')}:00</MenuItem>
                  ))}
                </TextField>
              </div>
            ))}
          </div>

          <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 700 }}>Mensagens e intervalos</Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
            Use {'{{name}}'} para o nome do contato. O intervalo indica quanto tempo ESPERAR antes de enviar cada mensagem.
          </Typography>

          {flowMessages.map((step, stepIdx) => (
            <div key={stepIdx} className={classes.messageBlock}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Typography variant="caption" style={{ fontWeight: 700 }}>Passo {stepIdx + 1}</Typography>
                {flowMessages.length > 1 && (
                  <IconButton size="small" onClick={() => removeMessage(stepIdx)}><DeleteIcon fontSize="small" /></IconButton>
                )}
              </div>

              {currentFlowType !== 'official' && (step.msgs || []).map((m, msgIdx) => (
                <Box key={msgIdx} style={{ background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <Chip size="small"
                      icon={m.type === 'text' ? <TextFieldsIcon style={{ fontSize: 14 }} /> : m.type === 'image' ? <ImageIcon style={{ fontSize: 14 }} /> : m.type === 'audio' ? <AudiotrackIcon style={{ fontSize: 14 }} /> : m.type === 'document' ? <PictureAsPdfIcon style={{ fontSize: 14 }} /> : <VideocamIcon style={{ fontSize: 14 }} />}
                      label={m.type === 'text' ? 'Texto' : m.type === 'image' ? 'Imagem' : m.type === 'audio' ? 'Áudio' : m.type === 'document' ? 'Material PDF' : 'Vídeo'}
                      style={{ fontSize: 11 }}
                    />
                    {(step.msgs || []).length > 1 && (
                      <IconButton size="small" onClick={() => removeMsgFromStep(stepIdx, msgIdx)}><CloseIcon style={{ fontSize: 14 }} /></IconButton>
                    )}
                  </Box>

                  {m.type === 'text' ? (
                    <TextField
                      value={m.content || ''}
                      onChange={e => updateMsgInStep(stepIdx, msgIdx, 'content', e.target.value)}
                      multiline rows={3} fullWidth variant="outlined" size="small"
                      placeholder="Texto... use {{name}} para o nome do contato"
                    />
                  ) : (
                    <Box>
                      {m.uploading && <LinearProgress style={{ marginBottom: 4 }} />}
                      {m.url ? (
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Typography variant="caption" style={{ flex: 1, wordBreak: 'break-all', opacity: 0.8 }}>{m.filename || 'arquivo enviado'}</Typography>
                          <IconButton size="small" onClick={() => { updateMsgInStep(stepIdx, msgIdx, 'url', ''); updateMsgInStep(stepIdx, msgIdx, 'filename', ''); }}><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                      ) : (
                        <Button variant="outlined" size="small" component="label" disabled={!!m.uploading}
                          style={{ borderRadius: 6, textTransform: 'none', fontSize: 12 }}>
                          {m.type === 'image' ? 'Enviar imagem' : m.type === 'audio' ? 'Enviar áudio' : m.type === 'document' ? 'Enviar material PDF' : 'Enviar vídeo'}
                          <input type="file" hidden
                            accept={m.type === 'image' ? 'image/*' : m.type === 'audio' ? 'audio/*' : m.type === 'document' ? 'application/pdf,.pdf' : 'video/*'}
                            onChange={e => { if (e.target.files && e.target.files[0]) handleFileUpload(stepIdx, msgIdx, e.target.files[0], m.type); }}
                          />
                        </Button>
                      )}
                      {m.type !== 'audio' && (
                        <TextField label="Legenda (opcional)" size="small" variant="outlined" fullWidth
                          value={m.content || ''}
                          onChange={e => updateMsgInStep(stepIdx, msgIdx, 'content', e.target.value)}
                          placeholder="Legenda opcional..."
                          style={{ marginTop: 6 }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ))}

              {currentFlowType !== 'official' && <Box style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {[
                  { type: 'text', icon: <TextFieldsIcon style={{ fontSize: 13 }} />, label: '+Texto' },
                  { type: 'image', icon: <ImageIcon style={{ fontSize: 13 }} />, label: '+Imagem' },
                  { type: 'audio', icon: <AudiotrackIcon style={{ fontSize: 13 }} />, label: '+Audio' },
                  { type: 'video', icon: <VideocamIcon style={{ fontSize: 13 }} />, label: '+Video' },
                  { type: 'document', icon: <PictureAsPdfIcon style={{ fontSize: 13 }} />, label: '+Material PDF' },
                ].map(btn => (
                  <Button key={btn.type} size="small" variant="outlined"
                    startIcon={btn.icon}
                    onClick={() => addMsgToStep(stepIdx, btn.type)}
                    style={{ borderRadius: 6, textTransform: 'none', fontSize: 11, padding: '2px 8px' }}>
                    {btn.label}
                  </Button>
                ))}
              </Box>}

              <div className={classes.delayRow}>
                <Typography className={classes.delayLabel}>
                  Enviar no DIA:
                </Typography>
                <TextField
                  label="Dia" type="number" value={flowMessages.slice(0, stepIdx + 1).reduce((s, m) => s + (Number(m.delayDays) || 0), 0)}
                  onChange={e => { const _abs = Number(e.target.value); const _prev = flowMessages.slice(0, stepIdx).reduce((s, m) => s + (Number(m.delayDays) || 0), 0); updateMessage(stepIdx, 'delayDays', Math.max(0, _abs - _prev)); }}
                  variant="outlined" size="small" style={{ width: 80 }} inputProps={{ min: 0 }}
                />
                <TextField
                  label="Horas" type="number" value={step.delayHours}
                  onChange={e => updateMessage(stepIdx, 'delayHours', Number(e.target.value))}
                  variant="outlined" size="small" style={{ width: 80 }} inputProps={{ min: 0, max: 23 }}
                />
                <TextField
                  label="Minutos" type="number" value={step.delayMinutes || 0}
                  onChange={e => updateMessage(stepIdx, 'delayMinutes', Number(e.target.value))}
                  variant="outlined" size="small" style={{ width: 90 }} inputProps={{ min: 0, max: 59 }}
                />
              </div>
              {(() => { const _d = flowMessages.slice(0, stepIdx + 1).reduce((s, m) => s + (Number(m.delayDays) || 0), 0); return (<Typography variant="caption" style={{ display: 'block', marginTop: 4, color: '#888' }}>(= aprox. {_d * 24}h apos o inicio do follow-up)</Typography>); })()}

              <Box style={{ marginTop: 8, padding: '8px 10px', background: currentFlowType === 'official' ? 'rgba(25,118,210,0.06)' : 'rgba(0,135,90,0.06)', borderRadius: 6, border: currentFlowType === 'official' ? '1.5px solid rgba(25,118,210,0.35)' : '1px solid rgba(0,135,90,0.2)' }}>
                <Typography variant="caption" style={{ fontWeight: 700, color: currentFlowType === 'official' ? '#1976d2' : '#00875A', display: 'block', marginBottom: 6 }}>
                  {currentFlowType === 'official' ? 'Template Meta — obrigatorio neste passo' : 'Template Meta (API Oficial) - opcional por passo'}
                </Typography>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Template aprovado pela Meta</InputLabel>
                  <Select
                    value={step.apiTemplateId || ''}
                    onChange={e => updateMessage(stepIdx, 'apiTemplateId', e.target.value || null)}
                    label="Template aprovado pela Meta"
                  >
                    {currentFlowType !== 'official' && <MenuItem value=""><em>Nenhum (QR Code e API dentro de 24h usam texto acima)</em></MenuItem>}
                    {currentFlowType === 'official' && <MenuItem value=""><em>Selecione um template aprovado...</em></MenuItem>}
                    {apiTemplates.filter(t => (t.status || '').toUpperCase() === 'APPROVED').map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.shortcode} ({t.category})</MenuItem>
                    ))}
                    {apiTemplates.filter(t => (t.status || '').toUpperCase() === 'APPROVED').length === 0 && (
                      <MenuItem disabled value=""><em>Nenhum template aprovado. Sincronize em Templates Meta.</em></MenuItem>
                    )}
                  </Select>
                </FormControl>
                <Typography variant="caption" style={{ opacity: 0.7, display: 'block', marginTop: 4 }}>
                  {currentFlowType === 'official' ? 'Fluxo API Oficial — cada passo envia este template diretamente.' : 'Se selecionado: conexoes API Oficial enviam este template. QR Code usa o texto acima.'}
                </Typography>
              </Box>
            </div>
          ))}

          <Button
            onClick={addMessage}
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            style={{ borderRadius: 8, textTransform: 'none' }}
          >
            {currentFlowType === 'official' ? 'Adicionar passo (template)' : 'Adicionar passo'}
          </Button>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setFlowsOpen(false)} variant="outlined" style={{ borderRadius: 8, textTransform: 'none' }}>
            Fechar
          </Button>
          <Button
            onClick={saveFlow}
            variant="contained"
            color="primary"
            disabled={saving}
            style={{ borderRadius: 8, textTransform: 'none', fontWeight: 700 }}
          >
            {saving ? 'Salvando...' : editFlow ? 'Salvar alterações' : 'Criar fluxo'}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Type Picker Dialog */}
      <Dialog open={typeModalOpen} onClose={() => setTypeModalOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}>
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8 }}>
          <span style={{ fontWeight: 800, fontSize: '1rem' }}>Tipo de Fluxo</span>
          <IconButton size="small" onClick={() => setTypeModalOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: '0.88rem', color: '#666', marginBottom: 20 }}>
            Escolha o tipo de canal que este fluxo de follow-up vai utilizar:
          </Typography>
          <Box style={{ display: 'flex', gap: 16, flexDirection: 'column' }}>
            <Box
              onClick={() => handleTypeSelected('qrcode')}
              style={{
                border: '2px solid rgba(0,135,90,0.4)', borderRadius: 12, padding: '18px 20px',
                cursor: 'pointer', transition: 'all 0.15s',
                background: 'rgba(0,135,90,0.05)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,135,90,0.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,135,90,0.05)'}
            >
              <Typography style={{ fontWeight: 800, fontSize: '0.95rem', color: '#00875A', marginBottom: 4 }}>
                📱 QR Code / WhatsApp normal
              </Typography>
              <Typography style={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.5 }}>
                Para conexões WhatsApp conectadas via QR Code. Suporta texto, imagem, áudio, vídeo e material PDF. Também funciona em Instagram e Facebook com texto.
              </Typography>
            </Box>
            <Box
              onClick={() => handleTypeSelected('official')}
              style={{
                border: '2px solid rgba(25,118,210,0.4)', borderRadius: 12, padding: '18px 20px',
                cursor: 'pointer', transition: 'all 0.15s',
                background: 'rgba(25,118,210,0.04)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(25,118,210,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(25,118,210,0.04)'}
            >
              <Typography style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1976d2', marginBottom: 4 }}>
                🔵 WhatsApp API Meta (Oficial)
              </Typography>
              <Typography style={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.5 }}>
                Para conexões via API Oficial da Meta. Cada passo usa um template aprovado; mídia somente pelo cabeçalho do próprio template Meta.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setTypeModalOpen(false)} variant="outlined"
            style={{ borderRadius: 8, textTransform: 'none' }}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Connection Modal */}
      <Dialog open={connModalOpen} onClose={() => setConnModalOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700 }}>Conexões: {connFlow && connFlow.name}</span>
          <IconButton size="small" onClick={() => setConnModalOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ fontSize: "0.8rem", color: "#555", marginBottom: 12 }}>
            Selecione em quais conexões este fluxo deve aparecer. Se nenhuma for selecionada, aparece em <strong>todas</strong>.
          </Typography>
          {availableWhatsapps.map(w => (
            <FormControlLabel key={w.id}
              control={
                <Checkbox
                  checked={selectedConns.includes(w.id)}
                  onChange={e => {
                    if (e.target.checked) setSelectedConns(prev => [...prev, w.id]);
                    else setSelectedConns(prev => prev.filter(id => id !== w.id));
                  }}
                  color="primary" size="small"
                />
              }
              label={
                <span style={{ fontSize: "0.85rem" }}>
                  {w.name}
                  <span style={{ marginLeft: 6, fontSize: "0.72rem",
                    color: w.status === "CONNECTED" ? "#00875A" : "#999" }}>
                    {w.status === "CONNECTED" ? "● conectada" : "○ desconectada"}
                  </span>
                </span>
              }
              style={{ display: "flex", marginBottom: 4 }}
            />
          ))}
          {availableWhatsapps.length === 0 && (
            <Typography style={{ color: "#999", fontSize: "0.8rem" }}>Nenhuma conexão encontrada.</Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setConnModalOpen(false)} variant="outlined"
            style={{ borderRadius: 8, textTransform: "none" }}>Fechar</Button>
          <Button onClick={saveConnections} variant="contained" color="primary"
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 700 }}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </div>
    </MainContainer>
  );
};

export default FollowUp;
